import useHasAccess from "@hooks/useHasAccess"
import useSlugExtractor from "@hooks/useSlugExtractor"

import { NavTopBar, Table } from "@organisms"
import TableViewPhase from "@organisms/Table/TableViewControls/TableViewPhase/TableViewPhase"
import TableViewPhases from "@organisms/Table/TableViewControls/TableViewPhases/TableViewPhases"

import { CLIENTS_ROUTES } from "@routes/clients"

import { BillingIntentions } from "@constants/choices"
import { PERMISSION_LEVEL } from "@constants/permissionLevel"
import {
    CLIENTS_LIST_CONTROLS_ACTIVE_TAB,
    CLIENTS_LIST_CONTROLS_KEYWORD_SEARCH,
    CLIENTS_LIST_CONTROLS_ORDERING,
    CLIENTS_LIST_CONTROLS_PHASE,
} from "@constants/storage"

import { ClientsTabs } from "./ClientTableViewTabs"

export default function ClientTableView() {
    const [createRoute] = useSlugExtractor([CLIENTS_ROUTES.CREATE])
    const { hasPermission } = useHasAccess()

    function getBillsTo(client: Client) {
        const intention = client.default_billing_intention

        if (intention === BillingIntentions.SERVICE_LOCATION) {
            return null
        } else if (intention === BillingIntentions.SERVICE_CLIENT) {
            return "This Client"
        } else if (intention === BillingIntentions.DIFFERENT_CLIENT) {
            return client.default_billing_client_name
        }

        return null
    }

    return (
        <Table.Provider
            activePhaseStorageKey={CLIENTS_LIST_CONTROLS_PHASE}
            activeTabStorageKey={CLIENTS_LIST_CONTROLS_ACTIVE_TAB}
            searchKeywordsStorageKey={CLIENTS_LIST_CONTROLS_KEYWORD_SEARCH}
            orderingStorageKey={CLIENTS_LIST_CONTROLS_ORDERING}
            listTitle="Clients"
            objectType="client"
            objectCreateRoute={createRoute}
            objectIndexName="Client"
            objectName="Client"
            defaultActivePhase="active"
            isRowClickable={hasPermission("clients_view_permission", PERMISSION_LEVEL.FULL)}
            isEmptyStateCreateButtonVisible={hasPermission("clients_create_permission", PERMISSION_LEVEL.FULL)}
        >
            <NavTopBar.Portal>
                <NavTopBar.Body>
                    <NavTopBar.BodyStart>
                        <TableViewPhases>
                            <TableViewPhase phase="active" title="Active" />
                            <TableViewPhase phase="inactive" title="Inactive" />
                        </TableViewPhases>
                        <NavTopBar.VerticalDivider />
                        <ClientsTabs />
                    </NavTopBar.BodyStart>
                    <NavTopBar.BodyEnd>
                        <Table.SearchInput />
                        <Table.ViewMenu />
                    </NavTopBar.BodyEnd>
                </NavTopBar.Body>
            </NavTopBar.Portal>
            <Table.Table>
                <Table.Column<Client>
                    accessorKey="name"
                    header="Client"
                    size={224}
                    cell={({ getValue }) => <Table.Cells.TextCell content={getValue()} />}
                    enableSorting={true}
                    enablePinning={true}
                    dataType="alphabetical"
                />
                <Table.Column<Client>
                    accessorKey="client_type_label"
                    header="Type"
                    size={112}
                    cell={({ getValue }) => <Table.Cells.TextCell content={getValue()} />}
                    enableSorting={true}
                    enablePinning={true}
                    dataType="other"
                />
                <Table.Column<Client>
                    accessorKey="service_locations_display"
                    header="Location"
                    size={288}
                    cell={({ getValue }) => <Table.Cells.TextCell content={getValue()} />}
                    enableSorting={false}
                    enablePinning={false}
                    dataType="alphabetical"
                />
                <Table.Column<Client>
                    accessorKey="bills_to"
                    header="Bills To"
                    size={224}
                    cell={({ row }) => <Table.Cells.TextCell content={getBillsTo(row.original)} />}
                    enableSorting={true}
                    enablePinning={false}
                    dataType="alphabetical"
                />
                <Table.Column<Client>
                    accessorKey="default_invoice_net"
                    header="Terms"
                    size={144}
                    cell={({ getValue }) => <Table.Cells.PaymentTermsCell terms={getValue()} />}
                    enableSorting={true}
                    enablePinning={false}
                    dataType="numerical"
                />
                <Table.Column<Client>
                    accessorKey="credit_limit"
                    header="Credit Limit"
                    size={144}
                    cell={({ getValue }) => <Table.Cells.NumericalDataCell value={getValue()} type="currency" />}
                    enableSorting={true}
                    enablePinning={false}
                    dataType="numerical"
                    alignment="right"
                />
                <Table.Column<Client>
                    accessorKey="labels"
                    header="Labels"
                    size={240}
                    cell={({ getValue }) => (
                        <Table.Cells.BadgesCell
                            badges={(getValue() as ClientLabel[])?.map((label: ClientLabel) => label.name)}
                        />
                    )}
                    enableSorting={false}
                    enablePinning={false}
                    dataType="other"
                    loadingType="badge"
                />
                <Table.Column<Client>
                    accessorKey="last_modified_at"
                    header="Last Modified"
                    size={144}
                    cell={({ getValue }) => <Table.Cells.DateTimeCell value={getValue()} />}
                    enableSorting={true}
                    enablePinning={false}
                    dataType="timing"
                />
            </Table.Table>
        </Table.Provider>
    )
}
